// frontend/src/pages/MirrorPromptsPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Download, RefreshCw, Sparkles, Square, X } from "lucide-react";

import { useSelfieCamera } from "../hooks/useSelfieCamera";
import { MIRROR_PROMPTS, DAILY_MIRROR_STORAGE_KEYS } from "../content/dailyMirror";
import { daysBetweenISO, getOrInitStartDateISO, todayISO } from "../utils/dailyMirror";

function readLastPromptState() {
  try {
    const raw = localStorage.getItem(DAILY_MIRROR_STORAGE_KEYS.lastPromptShownAt);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return {
      dateISO: typeof obj.dateISO === "string" ? obj.dateISO : null,
      index: Number.isInteger(obj.index) ? obj.index : null,
      changed: !!obj.changed
    };
  } catch {
    return null;
  }
}

function writeLastPromptState({ index, changed }) {
  const payload = { dateISO: todayISO(), index, changed: !!changed };
  localStorage.setItem(
    DAILY_MIRROR_STORAGE_KEYS.lastPromptShownAt,
    JSON.stringify(payload)
  );
}

/**
 * Mirror Prompts — mobile-first selfie reflection
 * - Camera off: full-viewport prompt + prominent "Start Mirror" CTA
 * - Camera on: Clean Mirror Mode + tap-to-show frosted bottom dock (3s auto-hide)
 */
export default function MirrorPromptsPage() {
  const navigate = useNavigate();
  const {
    videoRef,
    isCameraOn,
    isRequesting,
    error,
    startStream,
    stopStream
  } = useSelfieCamera();

  const [controlsVisible, setControlsVisible] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const hideTimerRef = useRef(null);

  const handleExitToDashboard = () => navigate("/dashboard");
  const handleSmartBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/daily-mirror");
  };

  // Deterministic "daily" base prompt index
  const baseIndex = useMemo(() => {
    const startISO = getOrInitStartDateISO();
    const idx = daysBetweenISO(startISO, todayISO());
    return ((idx % MIRROR_PROMPTS.length) + MIRROR_PROMPTS.length) % MIRROR_PROMPTS.length;
  }, []);

  const [promptIndex, setPromptIndex] = useState(() => {
    const last = readLastPromptState();
    if (last?.dateISO === todayISO() && Number.isInteger(last.index)) {
      if (last.index >= 0 && last.index < MIRROR_PROMPTS.length) return last.index;
    }
    writeLastPromptState({ index: baseIndex, changed: false });
    return baseIndex;
  });

  const alreadyChangedToday = useMemo(() => {
    const last = readLastPromptState();
    return last?.dateISO === todayISO() ? !!last?.changed : false;
    // eslint-disable-next-line
  }, [promptIndex]);

  const prompt = useMemo(() => {
    return MIRROR_PROMPTS[promptIndex] || MIRROR_PROMPTS[0];
  }, [promptIndex]);

  const handleNewPrompt = () => {
    if (alreadyChangedToday) return;
    const nextIdx = (baseIndex + 7) % MIRROR_PROMPTS.length;
    setPromptIndex(nextIdx);
    writeLastPromptState({ index: nextIdx, changed: true });
  };

  // Local-only branded snapshot (no upload)
  const snapFrame = useCallback(async () => {
    if (snapping) return;
    const video = videoRef?.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;
    setSnapping(true);
    try {
      const W = 1080;
      const H = 1920;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, W, H);

      // Selfie (mirrored, object-cover)
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.max(W / vw, H / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      const dx = (W - dw) / 2;
      const dy = (H - dh) / 2;
      ctx.save();
      ctx.translate(W, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, W - dx - dw, dy, dw, dh);
      ctx.restore();

      // Bottom dark gradient
      const bg = ctx.createLinearGradient(0, H * 0.6, 0, H);
      bg.addColorStop(0, "rgba(0,0,0,0)");
      bg.addColorStop(1, "rgba(0,0,0,0.85)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, H * 0.6, W, H * 0.4);

      // Top dark gradient
      const tg = ctx.createLinearGradient(0, 0, 0, H * 0.55);
      tg.addColorStop(0, "rgba(0,0,0,0.7)");
      tg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = tg;
      ctx.fillRect(0, 0, W, H * 0.55);

      // Prompt text (upper-third)
      ctx.font = "700 60px 'Inter', system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0,0,0,0.65)";
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 4;
      const maxW = W - 160;
      const words = prompt.split(" ");
      const lines = [];
      let line = "";
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxW && line) {
          lines.push(line);
          line = w;
        } else {
          line = test;
        }
      }
      if (line) lines.push(line);
      const lineH = 80;
      const startY = H * 0.32 - (lines.length * lineH) / 2;
      lines.forEach((ln, i) => ctx.fillText(ln, W / 2, startY + i * lineH + lineH / 2));
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Brand mark (bottom-right)
      ctx.textAlign = "right";
      ctx.textBaseline = "alphabetic";
      ctx.font = "600 32px 'Inter', system-ui, -apple-system, sans-serif";
      const brandText = "UnityLove";
      const brandW = ctx.measureText(brandText).width;
      const bx = W - 60;
      const by = H - 90;

      // Heart
      ctx.fillStyle = "#FF4F7A";
      ctx.beginPath();
      const cx = bx - brandW - 18;
      const cy = by - 22;
      const s = 14;
      ctx.moveTo(cx, cy + s * 0.25);
      ctx.bezierCurveTo(cx, cy, cx - s, cy, cx - s, cy + s * 0.35);
      ctx.bezierCurveTo(cx - s, cy + s * 0.7, cx, cy + s * 0.95, cx, cy + s * 1.2);
      ctx.bezierCurveTo(cx, cy + s * 0.95, cx + s, cy + s * 0.7, cx + s, cy + s * 0.35);
      ctx.bezierCurveTo(cx + s, cy, cx, cy, cx, cy + s * 0.25);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillText(brandText, bx, by);
      ctx.font = "500 24px 'Inter', system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.fillText("joinunitylove.com", bx, by + 36);

      const link = document.createElement("a");
      link.download = `unitylove-mirror-prompt-${todayISO()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Snap failed:", err);
    } finally {
      setSnapping(false);
    }
  }, [snapping, videoRef, prompt]);

  // ---------- Auto-hide controls ----------
  const toggleControls = useCallback(() => {
    if (!isCameraOn) return; // tap-toggle only matters once mirror is live
    try { if (navigator.vibrate) navigator.vibrate(10); } catch (_) {}
    setControlsVisible(prev => {
      if (!prev) {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
        return true;
      }
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      return false;
    });
  }, [isCameraOn]);

  const bumpTimer = useCallback(() => {
    if (controlsVisible && isCameraOn) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
    }
  }, [controlsVisible, isCameraOn]);

  // Auto-hide once camera turns on (defaults to clean mode after 1s reveal)
  useEffect(() => {
    if (isCameraOn) {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [isCameraOn]);

  const handleStartMirror = (e) => {
    e?.stopPropagation?.();
    startStream();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div
        className="relative overflow-hidden bg-black select-none"
        style={{ minHeight: "100dvh" }}
        onClick={toggleControls}
        data-testid="mirror-prompts-stage"
      >
        {/* Camera feed (mirrored) */}
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="h-full w-full object-cover -scale-x-100"
          />
        </div>

        {/* Cinematic vignettes */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-black/65 via-black/25 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* Top nav (always visible) */}
        <div className="absolute left-0 right-0 top-0 z-20 px-4 pt-[max(env(safe-area-inset-top),16px)]">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-black/45 p-2.5 text-white/90 backdrop-blur-md hover:bg-black/55 transition active:scale-95"
              onClick={(e) => { e.stopPropagation(); handleSmartBack(); }}
              aria-label="Back"
              data-testid="prompts-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="rounded-full bg-black/40 px-3.5 py-1.5 text-xs font-medium tracking-wide text-white/85 backdrop-blur-md">
              Mirror Prompt
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-black/45 p-2.5 text-white/90 backdrop-blur-md hover:bg-black/55 transition active:scale-95"
              onClick={(e) => { e.stopPropagation(); handleExitToDashboard(); }}
              aria-label="Exit"
              data-testid="prompts-exit-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Prompt text — upper-third, never covers face */}
        <div className="pointer-events-none absolute inset-x-0 top-[20%] px-8 text-center z-10">
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold leading-snug text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.75)] max-w-xl mx-auto">
            {prompt}
          </p>
          {alreadyChangedToday && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] uppercase tracking-wider text-white/65 backdrop-blur-md">
              One change per day
            </div>
          )}
        </div>

        {/* "Start Mirror" centered CTA when camera is off */}
        {!isCameraOn && !isRequesting && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 px-6 text-center">
            <button
              type="button"
              onClick={handleStartMirror}
              className="inline-flex items-center gap-2 rounded-full bg-rose-500/90 hover:bg-rose-500 px-6 py-3 text-white font-medium shadow-[0_8px_30px_rgba(255,79,122,0.4)] backdrop-blur-md transition active:scale-95"
              data-testid="prompts-start-mirror-cta"
            >
              <Camera className="h-5 w-5" />
              Start Mirror
            </button>
            <p className="mt-3 text-xs text-white/55">
              Camera is local-only. We never record or upload.
            </p>
          </div>
        )}

        {isRequesting && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 text-center text-white/70 text-sm">
            Requesting camera…
          </div>
        )}

        {/* Subtle brand mark (bottom-right, always) */}
        <div className="pointer-events-none absolute right-4 bottom-[max(env(safe-area-inset-bottom),16px)] z-10 text-right">
          <div className="flex items-center justify-end gap-1.5 text-white/85">
            <span className="text-rose-400 text-sm leading-none">♥</span>
            <span className="text-sm font-semibold tracking-tight">UnityLove</span>
          </div>
          <div className="text-[10px] text-white/50 tracking-wide">joinunitylove.com</div>
        </div>

        {/* Tap hint when camera on + controls hidden */}
        {isCameraOn && (
          <div
            className={[
              "pointer-events-none absolute inset-x-0 bottom-[max(env(safe-area-inset-bottom),20px)] z-10 transition-opacity duration-300 text-center",
              controlsVisible ? "opacity-0" : "opacity-100"
            ].join(" ")}
          >
            <span className="text-[11px] text-white/45 tracking-wide">Tap to show controls</span>
          </div>
        )}

        {/* Camera error */}
        {error && (
          <div className="absolute inset-x-0 bottom-28 px-6 z-10">
            <div className="mx-auto max-w-sm rounded-xl border border-rose-200/20 bg-rose-500/15 px-3 py-2 text-sm text-rose-200 backdrop-blur text-center">
              Camera unavailable: {error}
            </div>
          </div>
        )}

        {/* Bottom controls dock (only when camera on) */}
        {isCameraOn && (
          <div
            className={[
              "absolute inset-x-0 bottom-0 z-30 transition-all duration-300 ease-out",
              controlsVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6 pointer-events-none"
            ].join(" ")}
            data-testid="prompts-controls-dock"
          >
            <div
              className="mx-auto w-full max-w-md px-4 pb-[max(env(safe-area-inset-bottom),20px)] pt-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rounded-2xl border border-white/15 bg-black/55 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.4)]">
                <div className="flex items-center justify-between gap-1 px-2 py-3">
                  <DockButton
                    label={alreadyChangedToday ? "Locked" : "New"}
                    icon={<RefreshCw className="h-5 w-5" />}
                    disabled={alreadyChangedToday}
                    onClick={() => { handleNewPrompt(); bumpTimer(); }}
                    testId="prompts-new-btn"
                  />
                  <DockButton
                    label="Stop"
                    icon={<Square className="h-5 w-5" />}
                    onClick={() => { stopStream(); bumpTimer(); }}
                    testId="prompts-stop-btn"
                  />
                  <DockButton
                    label={snapping ? "..." : "Snap"}
                    icon={<Download className="h-5 w-5" />}
                    disabled={snapping}
                    onClick={() => { snapFrame(); bumpTimer(); }}
                    testId="prompts-snap-btn"
                  />
                  <DockButton
                    label="Affirm"
                    icon={<Sparkles className="h-5 w-5" />}
                    onClick={() => { navigate("/daily-mirror"); }}
                    testId="prompts-affirmations-btn"
                  />
                  <DockButton
                    label="Exit"
                    icon={<X className="h-5 w-5" />}
                    onClick={() => { handleExitToDashboard(); }}
                    testId="prompts-exit-dock-btn"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DockButton({ label, icon, onClick, active, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={[
        "flex flex-1 flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium transition active:scale-95",
        active
          ? "bg-emerald-500/25 text-emerald-200 border border-emerald-300/25"
          : "text-white/90 hover:bg-white/10 border border-transparent",
        disabled ? "opacity-50" : ""
      ].join(" ")}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
